import { useState, useEffect, useRef } from 'react';
import { Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { ProductCard } from './ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'ss_recently_viewed';
const MAX_ITEMS = 12;

export const addToRecentlyViewed = (productId) => {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const filtered = existing.filter(id => id !== productId);
    filtered.unshift(productId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered.slice(0, MAX_ITEMS)));
  } catch {
    // localStorage unavailable
  }
};

export const getRecentlyViewedIds = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

export const RecentlyViewed = ({ excludeId, limit = 6 }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      const ids = getRecentlyViewedIds().filter(id => id !== excludeId).slice(0, limit);
      if (ids.length === 0) { setLoading(false); return; }

      try {
        const responses = await Promise.all(
          ids.map(id => axios.get(`${API}/products/${id}`).catch(() => null))
        );
        setProducts(responses.filter(Boolean).map(r => r.data));
      } catch {
        // silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [excludeId, limit]);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll);
    return () => { if (el) el.removeEventListener('scroll', checkScroll); };
  }, [products]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-testid^="product-card-"]')?.offsetWidth || 240;
    el.scrollBy({ left: direction * (cardWidth + 24), behavior: 'smooth' });
  };

  if (loading || products.length === 0) return null;

  return (
    <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#F7F7EB]" data-testid="recently-viewed-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-[#DEA193]" />
            <h2 className="text-2xl tracking-tight">Continue Browsing</h2>
          </div>
          {/* Carousel arrows */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll(-1)}
              disabled={!canScrollLeft}
              className="w-9 h-9 flex items-center justify-center border border-[#E5D8D0] hover:border-[#000000] transition-colors disabled:opacity-30 disabled:cursor-default"
              data-testid="carousel-prev"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll(1)}
              disabled={!canScrollRight}
              className="w-9 h-9 flex items-center justify-center border border-[#E5D8D0] hover:border-[#000000] transition-colors disabled:opacity-30 disabled:cursor-default"
              data-testid="carousel-next"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Horizontal scrollable carousel */}
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map(product => (
            <div key={product.id} className="flex-shrink-0 w-[200px] sm:w-[220px] md:w-[240px] snap-start">
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
